import { db } from '@/firebase';
const billing_orders_ref = db.collection('billing_orders');

const createBillingOrder = async (proposalId, proposalData, billingData = null) => {
  try {
    // Usar total do billingData se fornecido, senão calcular
    const totalValue = billingData?.total 
      ? parseFloat(billingData.total) 
      : calculateTotalValue(proposalData);
    
    const billingOrder = {
      proposalId: proposalId,
      proposalData: proposalData,
      createdAt: new Date(),
      status: 'pending', // 'pending', 'processing', 'completed', 'cancelled'
      totalValue: totalValue,
      // Informações do lead
      id_lead: proposalData.id_lead,
      id_franchisee: proposalData.id_franchisee,
      // Informações do diagnóstico se existir
      id_diagnostic: proposalData.id_diagnostic || null,
    };

    // Adicionar dados de faturamento se fornecidos
    if (billingData) {
      billingOrder.paymentMethod = billingData.paymentMethod;
      billingOrder.billingType = billingData.billingType || 'single';
      billingOrder.dueDate = billingData.billingType === 'single' ? String(billingData.dueDate || '') : null;
      billingOrder.total = String(billingData.total || ''); // Garantir que seja string
      
      // Adicionar dados de parcelamento se for parcelado
      if (billingData.billingType === 'installments' && billingData.installments) {
        billingOrder.entryType = billingData.entryType || null;
        billingOrder.entryPercentage = billingData.entryPercentage || null;
        billingOrder.entryValue = billingData.entryValue || null;
        billingOrder.numberOfInstallments = billingData.numberOfInstallments || 0;
        billingOrder.installments = billingData.installments.map(inst => ({
          value: String(inst.value || '0'),
          dueDate: String(inst.dueDate || ''),
          type: inst.type || 'installment',
          number: inst.number || null
        }));
      }
      
      // Adicionar dados do usuário se fornecidos
      if (billingData.user) {
        billingOrder.user = {
          name: billingData.user.name || null,
          email: billingData.user.email || null,
          cpf: billingData.user.cpf || null,
          cnpj: billingData.user.cnpj || null,
          nameResponsible: billingData.user.nameResponsible || null,
          cpfResponsible: billingData.user.cpfResponsible || null,
          emailResponsible: billingData.user.emailResponsible || null,
          phoneResponsible: billingData.user.phoneResponsible || null,
          userType: billingData.user.userType || null,
          stateTaxNumber: billingData.user.stateTaxNumber || null,
          receiverStateTaxIndicator: billingData.user.receiverStateTaxIndicator || null,
          municipalTaxNumber: billingData.user.municipalTaxNumber || null
        };
      }
      
      // Adicionar dados de endereço se fornecidos
      if (billingData.address) {
        billingOrder.address = {
          street: billingData.address.street || null,
          city: billingData.address.city || null,
          district: billingData.address.district || null,
          state: billingData.address.state || null,
          apartmentNumber: billingData.address.apartmentNumber || null,
          zipCode: billingData.address.zipCode || null,
          residentialNumber: billingData.address.residentialNumber || null,
          complement: billingData.address.complement || null,
          country: billingData.address.country || 'Brasil',
          ibgeCityCode: billingData.address.ibgeCityCode || null
        };
      }
    }
    
    const docRef = await billing_orders_ref.add(billingOrder);
    const billingOrderId = docRef.id;
    
    // Atualizar o documento com o id_doc
    await editBillingOrder(billingOrderId, { id_doc: billingOrderId });
    
    // Atualizar a proposta para marcar como faturada
    await updateProposalBillingStatus(proposalId, proposalData, billingOrderId);
    
    return billingOrderId;
  } catch (error) {
    throw new Error('createBillingOrder Fail!' + error);
  }
};

const calculateTotalValue = (proposalData) => {
  let total = 0;
  
  // Calcular total de soluções
  if (proposalData.listSolutions && Array.isArray(proposalData.listSolutions)) {
    proposalData.listSolutions.forEach(solution => {
      total += (solution.total || 0);
    });
  }
  
  // Calcular total de serviços
  if (proposalData.listServices && Array.isArray(proposalData.listServices)) {
    proposalData.listServices.forEach(service => {
      total += (service.total || 0);
    });
  }
  
  // Calcular total de produtos
  if (proposalData.listProducts && Array.isArray(proposalData.listProducts)) {
    proposalData.listProducts.forEach(product => {
      total += (product.total || 0);
    });
  }
  
  return total;
};

const editBillingOrder = async (idDoc, data) => {
  try {
    await billing_orders_ref.doc(idDoc).update(data);
    return true;
  } catch (error) {
    throw new Error('editBillingOrder Fail!' + error);
  }
};

const getBillingOrderById = async (billingOrderId) => {
  try {
    const billingOrderDoc = await billing_orders_ref.doc(billingOrderId).get();
    if (!billingOrderDoc.exists) {
      throw new Error('Billing Order not found!');
    }
    const data = billingOrderDoc.data();
    // Garantir que o id_doc está presente
    return {
      ...data,
      id_doc: billingOrderDoc.id
    };
  } catch (error) {
    console.error('Error in getBillingOrderById:', error);
    throw error;
  }
};

const getBillingOrdersByProposalId = async (proposalId) => {
  let orders_list = [];
  try {
    const result = await billing_orders_ref.where('proposalId', '==', proposalId).get();
    result.forEach(doc => orders_list.push(doc.data()));
    return orders_list;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getBillingOrdersByLeadId = async (leadId) => {
  let orders_list = [];
  try {
    const result = await billing_orders_ref.where('id_lead', '==', leadId).get();
    result.forEach(doc => orders_list.push(doc.data()));
    return orders_list;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const updateProposalBillingStatus = async (proposalId, proposalData, billingOrderId) => {
  try {
    // Importar o serviço de proposta dinamicamente para evitar dependência circular
    const DiagnosticProposalService = (await import('@/services/firebase/DiagnosticProposal/DiagnosticProposalService')).default;
    
    const updateData = {
      status: 'billing_requested', // Status intermediário: faturamento solicitado
      billingRequestedAt: new Date(),
      billingOrderId: billingOrderId
    };
    
    // Se a proposta tem id_diagnostic, usar editWaterScanProposal, senão usar editProposal
    if (proposalData.id_diagnostic) {
      await DiagnosticProposalService.editWaterScanProposal(proposalId, { ...proposalData, ...updateData, id_doc: proposalId });
    } else {
      await DiagnosticProposalService.editProposal(proposalId, { ...proposalData, ...updateData, id_doc: proposalId });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating proposal billing status:', error);
    throw error;
  }
};

const updateProposalToBilled = async (proposalId, proposalData) => {
  try {
    // Importar o serviço de proposta dinamicamente para evitar dependência circular
    const DiagnosticProposalService = (await import('@/services/firebase/DiagnosticProposal/DiagnosticProposalService')).default;
    
    const updateData = {
      status: 'billed', // Status final: faturada (quando a cobrança for lançada)
      billedAt: new Date()
    };
    
    // Se a proposta tem id_diagnostic, usar editWaterScanProposal, senão usar editProposal
    if (proposalData.id_diagnostic) {
      await DiagnosticProposalService.editWaterScanProposal(proposalId, { ...proposalData, ...updateData, id_doc: proposalId });
    } else {
      await DiagnosticProposalService.editProposal(proposalId, { ...proposalData, ...updateData, id_doc: proposalId });
    }
    
    return true;
  } catch (error) {
    console.error('Error updating proposal to billed status:', error);
    throw error;
  }
};

export default {
  createBillingOrder,
  editBillingOrder,
  getBillingOrderById,
  getBillingOrdersByProposalId,
  getBillingOrdersByLeadId,
  updateProposalBillingStatus,
  updateProposalToBilled,
};

